import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AngularFireAuthGuard } from '@angular/fire/compat/auth-guard';
import { AuthPipe, redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { AdminGuard } from './users/admin.guard';
import {LoadImageComponent} from "./common/load-image/load-image.component";
import { FreemiumGuard } from './guards/subscription.guard';
import { OrganizationAdminGuard } from './organizations/organizations-admin.guard';
import { McpEnabledGuard } from './use-mcp/use-mcp.guard';
import { TikTokShopEnabledGuard } from './projects/tiktok-shop-scan/tiktok-shop-enabled.guard';

const redirectUnauthorizedToLogin = (): AuthPipe => redirectUnauthorizedTo(['/login']);

const routes: Routes = [
  {
    // Deliberately unguarded: the extension needs this route to answer "you are signed out" rather
    // than bounce to /login, so it can stop waiting instead of sitting out its timeout.
    path: 'extension-bridge',
    loadComponent: () =>
      import('./extension-bridge/extension-bridge.component').then((m) => m.ExtensionBridgeComponent),
    data: { title: 'Connect the Apex extension' },
  },
  {
    path: 'login',
    loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
  },
  {
    // The sign-in step of apex-mcp's OAuth flow. Unguarded like /login, and deliberately not
    // behind McpEnabledGuard: the flag gates who is shown the connector, not whether a user
    // who already has one can finish authorizing it.
    path: 'mcp-authorize',
    loadComponent: () =>
      import('./mcp-authorize/mcp-authorize.component').then((m) => m.McpAuthorizeComponent),
    data: { title: 'Connect to Apex' },
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard'
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./dashboardV2/dashboard.component').then((m) => m.DashboardV2Component),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Dashboard', description: 'Welcome to the dashboard' },
  },
  {
    path: 'keywords',
    loadChildren: () => import('./keywords/keywords.module').then((m) => m.KeywordsModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
  {
    path: 'load',
    pathMatch: 'full',
    redirectTo: 'load-image-from-path',
  },
  {
    path: 'load-image-from-path',
    component: LoadImageComponent,
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
  {
    path: 'subscription',
    loadChildren: () =>
      import('./subscription/subscription.module').then((m) => m.SubscriptionModule),
  },
  {
    path: 'profile',
    loadChildren: () => import('./profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Manage account' },
  },
  {
    path: 'dashboard/youtube-scan',
    loadChildren: () =>
      import('./projects/youtube-scan/youtube-scan.module').then((m) => m.YoutubeScanModule),
    canActivate: [AngularFireAuthGuard, FreemiumGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'YouTube scan' },
  },
  {
    path: 'dashboard/legacy-media-scan',
    loadChildren: () =>
      import('./projects/media-scan/media-scan.module').then((m) => m.MediaScanModule),
    canActivate: [AngularFireAuthGuard, FreemiumGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Media scan' },
  },
  {
    path: 'dashboard/media-scan',
    loadChildren: () =>
      import('./projects/media-scan/media-scan.module').then((m) => m.MediaScanModule),
    canActivate: [AngularFireAuthGuard, FreemiumGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Media scan' },
  },
  {
    path: 'dashboard/text-scan',
    loadChildren: () =>
      import('./projects/text-scan/text-scan.module').then((m) => m.TextScanModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Text scan' },
  },
  {
    path: 'dashboard/website-scan',
    loadChildren: () =>
      import('./projects/website-scan/website-scan.module').then((m) => m.WebsiteScanModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Website scan' },
  },
  {
    path: 'dashboard/document-scan',
    loadChildren: () =>
      import('./projects/document-scan/document-scan.module').then((m) => m.DocumentScanModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Documents scan' },
  },
  {
    path: 'dashboard/social-media-scan',
    loadChildren: () =>
      import('./projects/social-media-scan/social-media-scan.module').then((m) => m.SocialMediaScanModule),
    canActivate: [AngularFireAuthGuard, FreemiumGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Social media scan' },
  },
  {
    path: 'dashboard/amazon-scan',
    loadChildren: () =>
      import('./projects/amazon-scan/amazon-scan.module').then((m) => m.AmazonScanModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'Amazon scan' },
  },
  {
    path: 'dashboard/tiktok-shop-scan',
    loadChildren: () =>
      import('./projects/tiktok-shop-scan/tiktok-shop-scan.module').then((m) => m.TikTokShopScanModule),
    canActivate: [AngularFireAuthGuard, TikTokShopEnabledGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, isNew: true, title: 'TikTok Shop scan' },
  },
  {
    path: 'users',
    loadChildren: () => import('./users/users.module').then((m) => m.UsersModule),
    canActivate: [AngularFireAuthGuard, AdminGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Users' },
  },
  {
    path: 'flagged-websites',
    loadChildren: () => import('./flagged-websites/flagged-websites.module').then((m) => m.FlaggedWebsitesModule),
    canActivate: [AngularFireAuthGuard, AdminGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Flagged websites' },
  },
  {
    path: 'organizations',
    loadChildren: () => import('./organizations/organizations.module').then((m) => m.OrganizationsModule),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
  {
    path: 'accept-invitation',
    loadChildren: () => import('./invitations/accept-invitation/accept-invitation.module').then((m) => m.AcceptInvitationsModule),
  },
  {
    path: 'signup-org',
    loadChildren: () => import('./invitations/signup-org/signup-org.module').then((m) => m.SignupOrgModule),
  },
  {
    path: 'payment-issue',
    loadComponent: () => import('./payment-error/payment-error.component').then((m) => m.PaymentErrorComponent),
    canActivate: [],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Payment issue' },
  },
  {
    path: 'manage-subscription',
    loadComponent: () => import('./subscription-management/subscription-management.component').then((m) => m.SubscriptionManagementComponent),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Manage subscription' },
  },
  {
    path: 'ai-settings',
    loadComponent: () => import('./ai-settings/ai-settings.component').then((m) => m.AiSettingsComponent),
    canActivate: [AngularFireAuthGuard, AdminGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'AI settings' },
  },
  {
    path: 'ai-settings/:organizationId',
    loadComponent: () => import('./ai-settings-organization/ai-settings-organization.component').then((m) => m.AiSettingsComponent),
    canActivate: [AngularFireAuthGuard, OrganizationAdminGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'AI settings' },
  },
  {
    path: 'apex-nova-ai',
    loadComponent: () => import('./ai-chatbot/ai-chatbot.component').then((m) => m.AiChatbotComponent),
    canActivate: [AngularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'APEX NovaAI' },
  },
  {
    path: 'use-mcp',
    loadComponent: () => import('./use-mcp/use-mcp.component').then((m) => m.UseMcpComponent),
    canActivate: [AngularFireAuthGuard, McpEnabledGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'Use MCP' },
  },
  {
    path: 'admin-ai-conversations',
    loadComponent: () => import('./admin-conversations/admin-conversations.component').then((m) => m.AdminConversationsComponent),
    canActivate: [AngularFireAuthGuard, AdminGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, title: 'All conversations' },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
